import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { retailCustomersApi } from '../../api/customers.js';
import { usersApi } from '../../api/users.js';
import { areasApi } from '../../api/areas.js';
import { citiesApi } from '../../api/cities.js';

function PhoneCell({ phone }) {
  const [copiedIdx, setCopiedIdx] = useState(null);
  const numbers = phone ? phone.split(',').map(p => p.trim()).filter(Boolean) : [];
  if (numbers.length === 0) return <span style={{ color: '#94a3b8' }}>—</span>;

  const copy = (text, idx) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {numbers.map((n, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 13, fontFamily: 'monospace', color: '#1e293b' }}>{n}</span>
          <button
            onClick={() => copy(n, i)}
            style={{ background: copiedIdx === i ? '#dcfce7' : '#f1f5f9', border: `1px solid ${copiedIdx === i ? '#86efac' : '#e2e8f0'}`, borderRadius: 4, cursor: 'pointer', padding: '1px 6px', fontSize: 11, color: copiedIdx === i ? '#16a34a' : '#94a3b8', lineHeight: 1.6, flexShrink: 0 }}
          >
            {copiedIdx === i ? '✓' : '⎘'}
          </button>
        </div>
      ))}
    </div>
  );
}

function SearchableStaticSelect({ value, displayName, options, onChange, placeholder, style }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filtered = options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ position: 'relative', ...style }}>
      {(() => {
        const selOpt = value ? options.find(o => o.id === value) : null;
        return (
          <div
            className="form-control"
            onClick={() => setIsOpen(!isOpen)}
            style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: value ? '#334155' : '#94a3b8' }}>
              {selOpt?.dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: selOpt.dot, flexShrink: 0, display: 'inline-block' }} />}
              {value ? displayName : placeholder}
            </span>
            <span style={{ fontSize: 10, color: '#94a3b8' }}>▼</span>
          </div>
        );
      })()}

      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setIsOpen(false)} />
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, zIndex: 50, maxHeight: 240, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', minWidth: 160 }}>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', outline: 'none', fontSize: 13, borderRadius: '6px 6px 0 0', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', background: !value ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#64748b' }}
                onClick={() => { onChange(null); setIsOpen(false); setSearch(''); }}
              >
                {placeholder}
              </div>
              {filtered.map(o => (
                <div
                  key={o.id}
                  style={{ padding: '8px 12px', cursor: 'pointer', background: value === o.id ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#334155', display: 'flex', alignItems: 'center', gap: 8 }}
                  onClick={() => { onChange(o); setIsOpen(false); setSearch(''); }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = value === o.id ? '#f1f5f9' : 'transparent'}
                >
                  {o.dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: o.dot, flexShrink: 0 }} />}
                  {o.name}
                </div>
              ))}
              {filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: '#94a3b8' }}>No results</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SearchableAreaSelect({ value, areaName, onChange, parentId, style }) {
  const [search, setSearch] = useState('');
  const [areas, setAreas] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // reset cached results when zone changes so stale areas don't show on next open
  useEffect(() => { setAreas([]); }, [parentId]);

  useEffect(() => {
    if (!isOpen) return;
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const params = { limit: 20, search };
      if (parentId) {
        params.parent_id = parentId;
      } else {
        params.parent_id = 'not_null';
      }
      areasApi.getAll(params).then(res => {
        setAreas(res.data ?? res);
      }).catch(console.error).finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [search, isOpen, parentId]);

  return (
    <div style={{ position: 'relative', ...style }}>
      <div
        className="form-control"
        onClick={() => setIsOpen(!isOpen)}
        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        <span>{value ? areaName || 'Selected Area' : 'All Areas'}</span>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>▼</span>
      </div>

      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setIsOpen(false)} />
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, zIndex: 50, maxHeight: 240, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
            <input
              type="text"
              placeholder="Search area..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '10px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', outline: 'none', fontSize: 13, borderRadius: '6px 6px 0 0', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
              {/* clear option */}
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', background: !value ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#64748b' }}
                onClick={() => { onChange(null, null); setIsOpen(false); setSearch(''); }}
              >
                All Areas
              </div>
              {loading ? <div style={{ padding: '8px 12px', fontSize: 13, color: '#64748b' }}>Loading...</div> :
                areas.map(a => (
                  <div
                    key={a.id}
                    style={{ padding: '8px 12px', cursor: 'pointer', background: value === a.id ? '#f1f5f9' : 'transparent', fontSize: 13, borderRadius: 4, color: '#334155' }}
                    onClick={() => { onChange(a.id, a.name); setIsOpen(false); setSearch(''); }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = value === a.id ? '#f1f5f9' : 'transparent'}
                  >
                    {a.name}
                  </div>
                ))}
              {!loading && areas.length === 0 && <div style={{ padding: '8px 12px', fontSize: 13, color: '#64748b' }}>No areas found</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SearchableSalesSelect({ value, options, onChange, loading, placeholder = 'All Salespersons' }) {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filtered = options.filter(s =>
    s.name.toLowerCase().includes(search.toLowerCase())
  );
  const selected = options.find(s => s.id.toString() === value?.toString());
  const displayName = selected ? `${selected.name}${isTl(selected) ? ' (TL)' : ''}` : placeholder;

  return (
    <div style={{ position: 'relative', width: 180, flexShrink: 0 }}>
      <div
        className="form-control"
        onClick={() => !loading && setIsOpen(o => !o)}
        style={{ cursor: loading ? 'default' : 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: value ? 'inherit' : 'var(--text-muted)' }}>
          {loading ? 'Loading…' : displayName}
        </span>
        <span style={{ fontSize: 10, color: '#94a3b8', flexShrink: 0, marginLeft: 4 }}>▼</span>
      </div>

      {isOpen && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setIsOpen(false)} />
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 6, zIndex: 50, maxHeight: 240, display: 'flex', flexDirection: 'column', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', minWidth: 200 }}>
            <input
              type="text"
              placeholder="Search name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ padding: '8px 12px', border: 'none', borderBottom: '1px solid #f1f5f9', outline: 'none', fontSize: 13, borderRadius: '6px 6px 0 0', width: '100%', boxSizing: 'border-box' }}
              autoFocus
            />
            <div style={{ overflowY: 'auto', flex: 1, padding: 4 }}>
              <div
                style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderRadius: 4, color: '#64748b', background: !value ? '#f1f5f9' : 'transparent' }}
                onClick={() => { onChange(''); setIsOpen(false); setSearch(''); }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = !value ? '#f1f5f9' : 'transparent'}
              >
                All Salespersons
              </div>
              {filtered.map(s => (
                <div
                  key={s.id}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderRadius: 4, color: '#334155', background: value?.toString() === s.id.toString() ? '#f1f5f9' : 'transparent' }}
                  onClick={() => { onChange(s.id.toString()); setIsOpen(false); setSearch(''); }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = value?.toString() === s.id.toString() ? '#f1f5f9' : 'transparent'}
                >
                  {s.name}{isTl(s) ? ' (TL)' : ''}
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding: '8px 12px', fontSize: 13, color: '#64748b' }}>No results</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const isTl = (u) => {
  const roleName = u?.role?.name || u?.role || '';
  return roleName === 'sales_tl' || roleName === 'sales_teamlead';
};

const getSalesDisplayName = (u) => {
  if (!u) return '—';
  return isTl(u) ? `${u.name} (TL)` : u.name;
};

function SkeletonTable({ isAdmin }) {
  return (
    <>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .skeleton-line {
          background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 37%, #f1f5f9 63%);
          background-size: 400% 100%;
          animation: shimmer 1.4s ease infinite;
          border-radius: 4px;
        }
      `}</style>
      {[...Array(5)].map((_, idx) => (
        <tr key={idx}>
          <td><div className="skeleton-line" style={{ width: '80px', height: '16px' }} /></td>
          <td><div className="skeleton-line" style={{ width: '100px', height: '16px' }} /></td>
          <td><div className="skeleton-line" style={{ width: '90px', height: '16px' }} /></td>
          <td><div className="skeleton-line" style={{ width: '90px', height: '16px' }} /></td>
          <td><div className="skeleton-line" style={{ width: '120px', height: '16px' }} /></td>
          <td><div className="skeleton-line" style={{ width: '60px', height: '16px', borderRadius: '12px' }} /></td>
          <td><div className="skeleton-line" style={{ width: '70px', height: '16px' }} /></td>
          <td><div className="skeleton-line" style={{ width: '70px', height: '16px' }} /></td>
          {isAdmin && (
            <td>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                <div className="skeleton-line" style={{ width: '42px', height: '22px' }} />
                <div className="skeleton-line" style={{ width: '52px', height: '22px' }} />
              </div>
            </td>
          )}
        </tr>
      ))}
    </>
  );
}

function NewCustomerModal({ customer, onClose, onSave }) {
  const [form, setForm] = useState(
    customer
      ? {
        name: customer.name || '',
        phone: customer.phone || '',
        address: customer.address || '',
        remark: customer.remark || '',
        product_type: customer.product_type || '',
        rate: customer.rate || '',
        qty: customer.qty || '',
        lead_score: customer.lead_score || 'COLD',
        expected_order_value: customer.expected_order_value || '',
        source: customer.source || 'Imported',
        area_id: customer.area_id || '',
        area_name: customer.area?.name || '',
        area_parent_name: customer.area?.parent?.name || '',
        sales_id: customer.assigned_to || '',
        sales_name: customer.sales?.name || '',
      }
      : {
        name: '', phone: '', address: '', remark: '', product_type: '',
        rate: '', qty: '', lead_score: 'COLD', expected_order_value: '',
        source: 'Imported', area_id: '', area_name: '', area_parent_name: '',
        sales_id: '',
        sales_name: '',
      }
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [salesOptions, setSalesOptions] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);

  useEffect(() => {
    setSalesLoading(true);
    Promise.all([
      usersApi.getAll({ role: 'sales', limit: 200 }),
      usersApi.getAll({ role: 'sales_tl', limit: 200 })
    ])
      .then(([salesRes, tlRes]) => {
        const sales = salesRes.data ?? salesRes;
        const tls = tlRes.data ?? tlRes;
        setSalesOptions([...sales, ...tls].filter(u => u.is_active !== false));
      })
      .catch(console.error)
      .finally(() => setSalesLoading(false));
  }, []);

  const handleChange = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (form.area_id) {
      if (!form.area_parent_name) {
        areasApi.getById(form.area_id)
          .then(area => {
            setForm(f => ({ ...f, area_parent_name: area.parent?.name || '' }));
          })
          .catch(() => {
            setForm(f => ({ ...f, area_parent_name: '' }));
          });
      }
    } else {
      setForm(f => ({ ...f, area_parent_name: '' }));
    }
  }, [form.area_id]);

  const handleSave = async () => {
    if (!form.name?.trim()) { setErr('Customer Name is required.'); return; }
    if (!form.phone?.trim()) { setErr('Phone is required.'); return; }

    setSaving(true);
    setErr('');
    try {
      const payload = {
        ...form,
        assigned_to: form.sales_id ? parseInt(form.sales_id, 10) : null,
        rate: form.rate ? parseFloat(form.rate) : null,
        qty: form.qty ? parseInt(form.qty, 10) : null,
        expected_order_value: form.expected_order_value ? parseFloat(form.expected_order_value) : null,
        area_id: form.area_id ? parseInt(form.area_id, 10) : null,
      };
      await onSave(payload);
    } catch (e) {
      setErr(e.message || 'Save failed.');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '680px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <span style={{ fontWeight: 700 }}>{customer ? 'Edit Retail Customer' : 'Add Retail Customer'}</span>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && (
            <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13 }}>
              {err}
            </div>
          )}

          <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Basic Information</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input className="form-control" value={form.name} onChange={e => handleChange('name', e.target.value)} placeholder="Enter full name" />
            </div>
            <div className="form-group">
              <label className="form-label">Phone Number *</label>
              <input className="form-control" value={form.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="Phone number" />
            </div>

            <div className="form-group">
              <label className="form-label">Lead Score</label>
              <select className="form-control" value={form.lead_score} onChange={e => handleChange('lead_score', e.target.value)}>
                <option value="HOT">Hot</option>
                <option value="MEDIUM">Medium</option>
                <option value="COLD">Cold</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Assign To</label>
              <select className="form-control" value={form.sales_id || ''} onChange={e => {
                const id = e.target.value;
                const selected = salesOptions.find(s => s.id.toString() === id);
                handleChange('sales_id', id);
                handleChange('sales_name', selected?.name || '');
              }} disabled={salesLoading}>
                <option value="">Select Salesperson / Team Lead</option>
                {salesOptions.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}{isTl(s) ? ' (TL)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deal / Metric Details</div>
          <div className="grid-2" style={{ marginBottom: 20 }}>
            <div className="form-group">
              <label className="form-label">Product Type</label>
              <input className="form-control" value={form.product_type} onChange={e => handleChange('product_type', e.target.value)} placeholder="e.g. Cables, Pipes" />
            </div>
            <div className="form-group">
              <label className="form-label">Expected Value (₹)</label>
              <input className="form-control" type="number" value={form.expected_order_value} onChange={e => handleChange('expected_order_value', e.target.value)} placeholder="Expected order amount" />
            </div>
            <div className="form-group">
              <label className="form-label">Rate (₹)</label>
              <input className="form-control" type="number" value={form.rate} onChange={e => handleChange('rate', e.target.value)} placeholder="Price per unit" />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input className="form-control" type="number" value={form.qty} onChange={e => handleChange('qty', e.target.value)} placeholder="Unit quantity" />
            </div>
          </div>

          <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Area & Location</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">Area (Sub-zone)</label>
              <SearchableAreaSelect
                value={form.area_id}
                areaName={form.area_name}
                onChange={(id, name) => {
                  handleChange('area_id', id);
                  handleChange('area_name', name);
                }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Parent Area</label>
              <input className="form-control" value={form.area_parent_name || ''} readOnly placeholder="Parent Area" />
            </div>
            <div className="form-group">
              <label className="form-label">Full Address</label>
              <textarea className="form-control" rows={3} value={form.address} onChange={e => handleChange('address', e.target.value)} placeholder="Shop / Office address" />
            </div>
            <div className="form-group">
              <label className="form-label">Remark / Note</label>
              <textarea className="form-control" rows={2} value={form.remark} onChange={e => handleChange('remark', e.target.value)} placeholder="Special import notes or status description" />
            </div>
          </div>

        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

const CUSTOMER_TYPE_OPTIONS = [
  { id: 'Assigned',   name: 'Assigned' },
  { id: 'Dormant',    name: 'Dormant' },
  { id: 'XDifficult', name: 'XDifficult' },
  { id: 'Unassigned', name: 'Unassigned' },
];

const LEAD_SCORE_OPTIONS = [
  { id: 'HOT',       name: 'Hot',       dot: '#ef4444' },
  { id: 'MEDIUM',    name: 'Medium',    dot: '#f59e0b' },
  { id: 'COLD',      name: 'Cold',      dot: '#3b82f6' },
  { id: 'ONBOARDED', name: 'Onboarded', dot: '#22c55e' },
];

export default function NewCustomersPage({ role }) {
  const isAdmin = role === 'admin';
  const navigate = useNavigate();

  // ── customer list state ──────────────────────────────────────────────────────
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage]             = useState(1);
  const [limit]                     = useState(20);
  const [showModal, setShowModal]   = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);

  // ── cascading filter state ───────────────────────────────────────────────────
  const [cityFilter,         setCityFilter]         = useState(null);  // { id, name } | null
  const [areaFilter,         setAreaFilter]         = useState(null);  // { id, name } | null
  const [salesFilter,        setSalesFilter]        = useState('');    // user id string
  const [customerTypeFilter, setCustomerTypeFilter] = useState('');
  const [leadScoreFilter,    setLeadScoreFilter]    = useState('');
  const [search,             setSearch]             = useState('');

  // ── dropdown data ────────────────────────────────────────────────────────────
  const [cityOptions,  setCityOptions]  = useState([]);
  const [areaOptions,   setAreaOptions]   = useState([]);  // parent areas (zones)
  const [subAreaFilter, setSubAreaFilter] = useState(null); // { id, name } | null
  const [salesOptions,   setSalesOptions]   = useState([]);
  const [salesLoading,   setSalesLoading]   = useState(false);

  // load cities once
  useEffect(() => {
    citiesApi.getAll()
      .then(res => setCityOptions(res.data || []))
      .catch(console.error);
  }, []);

  // load all parent areas on mount
  useEffect(() => {
    areasApi.getAll({ parent_id: 'null', limit: 200 })
      .then(res => setAreaOptions(res.data || []))
      .catch(console.error);
  }, []);

  // city changed → reload zones for that city, reset zone + sub-area + sales
  useEffect(() => {
    setAreaFilter(null);
    setSubAreaFilter(null);
    setSalesFilter('');
    const params = cityFilter
      ? { city_id: cityFilter.id, parent_id: 'null', limit: 200 }
      : { parent_id: 'null', limit: 200 };
    areasApi.getAll(params)
      .then(res => setAreaOptions(res.data || []))
      .catch(console.error);
  }, [cityFilter]);


  // sub-area / zone / city changed → reload sales users at the most specific level
  useEffect(() => {
    setSalesFilter('');
    if (!isAdmin) return;
    setSalesLoading(true);
    const extraParam = subAreaFilter
      ? { area_id: subAreaFilter.id }   // most specific: child area
      : areaFilter
        ? { area_id: areaFilter.id }    // zone level
        : cityFilter
          ? { city_id: cityFilter.id }  // city level
          : {};                         // all users
    Promise.all([
      usersApi.getAll({ role: 'sales',    limit: 200, ...extraParam }),
      usersApi.getAll({ role: 'sales_tl', limit: 200, ...extraParam }),
    ])
      .then(([s, t]) => setSalesOptions([...(s.data ?? s), ...(t.data ?? t)].filter(u => u.is_active !== false)))
      .catch(console.error)
      .finally(() => setSalesLoading(false));
  }, [subAreaFilter, areaFilter, cityFilter, isAdmin]);


  const loadCustomers = async () => {
    setLoading(true);
    try {
      const res = await retailCustomersApi.getAll({
        page,
        limit,
        search:        search              || undefined,
        lead_score:    leadScoreFilter     || undefined,
        customer_type: customerTypeFilter  || undefined,
        assigned_to:   salesFilter         || undefined,
        area_id:       subAreaFilter?.id || areaFilter?.id || undefined,
        city:          cityFilter?.name  || undefined,
      });
      setCustomers(res.data || []);
      setTotalCount(res.count || 0);
    } catch (err) {
      console.error('Failed to load retail customers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delay = setTimeout(loadCustomers, 300);
    return () => clearTimeout(delay);
  }, [page, search, leadScoreFilter, salesFilter, customerTypeFilter, cityFilter, areaFilter, subAreaFilter]);

  const handleSave = async (formPayload) => {
    try {
      if (editCustomer) {
        await retailCustomersApi.update(editCustomer.id, formPayload);
      } else {
        await retailCustomersApi.create(formPayload);
      }
      setShowModal(false);
      setEditCustomer(null);
      await loadCustomers();
    } catch (err) {
      alert(err.message || 'Operation failed.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this retail customer?')) return;
    try {
      await retailCustomersApi.delete(id);
      await loadCustomers();
    } catch (err) {
      alert(err.message || 'Delete failed.');
    }
  };

  const totalPages = Math.ceil(totalCount / limit) || 1;

  return (
    <div className="page-body">
      {showModal && (
        <NewCustomerModal
          customer={editCustomer}
          onClose={() => { setShowModal(false); setEditCustomer(null); }}
          onSave={handleSave}
        />
      )}

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span className="card-title">Retail Customers</span>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--text-muted)' }}>Manage imported retail business contacts</p>
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-outline btn-sm" onClick={() => navigate('/customer-import')}>↑ Import CSV</button>
              <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>+ Add Contact</button>
            </div>
          )}
        </div>

        <div className="card-body">
          {/* ── Filter panel ──────────────────────────────────────────────────── */}
          {(() => {
            const anyActive = cityFilter || areaFilter || subAreaFilter || salesFilter || customerTypeFilter || leadScoreFilter || search;
            const clearAll  = () => { setCityFilter(null); setAreaFilter(null); setSubAreaFilter(null); setSalesFilter(''); setCustomerTypeFilter(''); setLeadScoreFilter(''); setSearch(''); setPage(1); };
            const LBL = ({ children }) => (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap' }}>{children}</span>
            );
            const ClearBtn = ({ show, onClick }) => show ? (
              <button onClick={onClick} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 14, lineHeight: 1, padding: '0 0 0 4px', display: 'flex', alignItems: 'center' }}>×</button>
            ) : null;
            return (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 16px', marginBottom: 20 }}>
                {/* header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.2"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#475569', letterSpacing: '0.01em' }}>Filters</span>
                    {anyActive && (
                      <span style={{ background: '#3b82f6', color: '#fff', fontSize: 10, fontWeight: 700, borderRadius: 10, padding: '1px 7px' }}>
                        {[cityFilter, areaFilter, subAreaFilter, salesFilter, customerTypeFilter, leadScoreFilter, search].filter(Boolean).length}
                      </span>
                    )}
                  </div>
                  {anyActive && (
                    <button onClick={clearAll} style={{ background: 'none', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', color: '#64748b', fontSize: 12, fontWeight: 500, padding: '3px 10px', display: 'flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ fontSize: 14, lineHeight: 1 }}>×</span> Clear all
                    </button>
                  )}
                </div>

                {/* filter controls */}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>City</LBL><ClearBtn show={!!cityFilter} onClick={() => { setCityFilter(null); setPage(1); }} />
                    </div>
                    <SearchableStaticSelect value={cityFilter?.id || null} displayName={cityFilter?.name || ''} options={cityOptions} onChange={city => { setCityFilter(city); setPage(1); }} placeholder="All Cities" style={{ width: 140 }} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Zone</LBL><ClearBtn show={!!areaFilter} onClick={() => { setAreaFilter(null); setSubAreaFilter(null); setSalesFilter(''); setPage(1); }} />
                    </div>
                    <SearchableStaticSelect value={areaFilter?.id || null} displayName={areaFilter?.name || ''} options={areaOptions} onChange={area => { setAreaFilter(area); setSubAreaFilter(null); setSalesFilter(''); setPage(1); }} placeholder="All Zones" style={{ width: 160 }} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Area</LBL><ClearBtn show={!!subAreaFilter} onClick={() => { setSubAreaFilter(null); setSalesFilter(''); setPage(1); }} />
                    </div>
                    <SearchableAreaSelect value={subAreaFilter?.id || null} areaName={subAreaFilter?.name || ''} parentId={areaFilter?.id || null} onChange={(id, name) => { setSubAreaFilter(id ? { id, name } : null); setSalesFilter(''); setPage(1); }} style={{ width: 175 }} />
                  </div>

                  {isAdmin && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <LBL>Sales Person</LBL><ClearBtn show={!!salesFilter} onClick={() => { setSalesFilter(''); setPage(1); }} />
                      </div>
                      <SearchableSalesSelect value={salesFilter} options={salesOptions} onChange={id => { setSalesFilter(id); setPage(1); }} loading={salesLoading} />
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Type</LBL><ClearBtn show={!!customerTypeFilter} onClick={() => { setCustomerTypeFilter(''); setPage(1); }} />
                    </div>
                    <SearchableStaticSelect value={customerTypeFilter || null} displayName={customerTypeFilter} options={CUSTOMER_TYPE_OPTIONS} onChange={o => { setCustomerTypeFilter(o?.id || ''); setPage(1); }} placeholder="All Types" style={{ width: 140 }} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Lead Score</LBL><ClearBtn show={!!leadScoreFilter} onClick={() => { setLeadScoreFilter(''); setPage(1); }} />
                    </div>
                    <SearchableStaticSelect value={leadScoreFilter || null} displayName={LEAD_SCORE_OPTIONS.find(o => o.id === leadScoreFilter)?.name || ''} options={LEAD_SCORE_OPTIONS} onChange={o => { setLeadScoreFilter(o?.id || ''); setPage(1); }} placeholder="All Scores" style={{ width: 140 }} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <LBL>Search</LBL><ClearBtn show={!!search} onClick={() => { setSearch(''); setPage(1); }} />
                    </div>
                    <div className="search-bar" style={{ width: 210, flexShrink: 0 }}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                      <input placeholder="Name, phone, address…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                    </div>
                  </div>

                </div>
              </div>
            );
          })()}

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Area</th>
                  <th>Parent Area</th>
                  <th>Assigned To</th>
                  <th
                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    onClick={() => {
                      const cycle = ['', 'HOT', 'MEDIUM', 'COLD', 'ONBOARDED'];
                      const next = cycle[(cycle.indexOf(leadScoreFilter) + 1) % cycle.length];
                      setLeadScoreFilter(next);
                      setPage(1);
                    }}
                  >
                    Lead Score{leadScoreFilter ? ` (${leadScoreFilter})` : ''} ↕
                  </th>
                  <th>Product Type</th>
                  <th>Expected Value</th>
                  {isAdmin && <th style={{ textAlign: 'center' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonTable isAdmin={isAdmin} />
                ) : customers.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin ? 9 : 8} style={{ textAlign: 'center', padding: '30px 0', color: 'var(--text-muted)' }}>
                      No contacts found
                    </td>
                  </tr>
                ) : (
                  customers.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td><PhoneCell phone={c.phone} /></td>
                      <td>{c.area?.name || '—'}</td>
                      <td style={{ fontSize: 12, textTransform: 'uppercase', color: 'var(--text-muted)' }}>{c.area?.parent?.name || '—'}</td>
                      <td style={{ fontSize: 13, color: c.sales ? '#334155' : '#94a3b8' }}>
                        {getSalesDisplayName(c.sales) || '—'}
                      </td>
                      <td>
                        {(() => {
                          const score = c.lead_score || 'COLD';
                          const scoreOpt = LEAD_SCORE_OPTIONS.find(o => o.id === score);
                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: scoreOpt?.dot || '#64748b', background: scoreOpt ? scoreOpt.dot + '18' : '#f1f5f9', border: `1px solid ${scoreOpt?.dot || '#e2e8f0'}40`, borderRadius: 20, padding: '2px 8px' }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: scoreOpt?.dot || '#64748b', flexShrink: 0 }} />
                              {scoreOpt?.name || score}
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ fontSize: 12 }}>{c.product_type || '—'}</td>
                      <td style={{ fontWeight: 600, color: 'var(--primary)' }}>
                        {c.expected_order_value ? `₹${Number(c.expected_order_value).toLocaleString('en-IN')}` : '—'}
                      </td>
                      {isAdmin && (
                        <td>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                            <button className="btn btn-outline btn-xs" onClick={() => { setEditCustomer(c); setShowModal(true); }}>Edit</button>
                            <button className="btn btn-outline btn-xs" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} onClick={() => handleDelete(c.id)}>Delete</button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginTop: 24, paddingBottom: 8 }}>
              <button
                className="btn btn-outline"
                disabled={page === 1 || loading}
                onClick={() => setPage(p => Math.max(p - 1, 1))}
              >
                Previous
              </button>
              <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 }}>
                Page {page} of {totalPages} (Showing {totalCount === 0 ? 0 : (page - 1) * limit + 1}–{Math.min(page * limit, totalCount)} of {totalCount} entries)
              </span>
              <button
                className="btn btn-outline"
                disabled={page >= totalPages || totalCount === 0 || loading}
                onClick={() => setPage(p => Math.min(p + 1, totalPages))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
